import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDownToLine, CheckCheck, ArrowDownWideNarrow, Loader2, Map as MapIcon, Route, Save, Trash2 } from "lucide-react";
import { orderByClosest } from "@/lib/routeOrder";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/realtime";
import { prefillFromRecord, shortDate, today } from "@/lib/invoice";
import DateField from "@/components/DateField";
import RoutePullBar from "@/components/routes/RoutePullBar";
import RouteStopsTable from "@/components/routes/RouteStopsTable";
import RouteResult from "@/components/routes/RouteResult";
import RouteMapPanel from "@/components/routes/RouteMapPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const jobKey = (s) => { const n = parseFloat(s.job); return Number.isNaN(n) ? [1, String(s.job || "").toLowerCase()] : [0, n]; };
const byJob = (a, b) => { const x = jobKey(a), y = jobKey(b); return x[0] - y[0] || (x[1] < y[1] ? -1 : x[1] > y[1] ? 1 : 0); };

export default function RouteSetup() {
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [name, setName] = useState("");
  const [stops, setStops] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkDate, setBulkDate] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const loadLists = () => api.get("/route-lists").then((r) => setLists(r.data)).catch((e) => toast.error(formatError(e)));
  const location = useLocation();
  useEffect(() => { loadLists(); }, []);
  useLiveRefresh(loadLists);
  useEffect(() => {
    const picked = location.state?.stops;
    if (!picked?.length) return;
    add(picked);
    toast.success(`Added ${picked.length} customer${picked.length === 1 ? "" : "s"} from the table${picked.some((s) => s.number) ? " with their invoice numbers" : ""}`);
    window.history.replaceState({}, "");
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (id) => {
    const l = lists.find((x) => x.id === id);
    setListId(id); setName(l?.name || ""); setStops((l?.stops || []).map((s) => ({ ...s, number: "" }))); setSelected(new Set()); setResult(null);
  };
  const reset = () => { setListId(""); setName(""); setStops([]); setSelected(new Set()); setResult(null); };

  const add = (found) => setStops((prev) => {
    const have = new Set(prev.map((s) => s.record_id));
    return [...prev, ...found.filter((s) => !have.has(s.record_id)).map((s) => ({ ...s, date: s.date || "", number: s.number || "" }))];
  });

  const targets = () => (selected.size ? stops.filter((s) => selected.has(s.record_id)) : stops);
  const applyDate = (v) => {
    setBulkDate(v);
    if (!v) return;
    const ids = new Set(targets().map((s) => s.record_id));
    setStops((prev) => prev.map((s) => (ids.has(s.record_id) ? { ...s, date: v } : s)));
  };

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(formatError(e)); } setBusy(""); };

  const closest = (start) => run("closest", async () => {
    const res = await orderByClosest(stops, start);
    setStops(res.stops);
    toast.success(`Ordered from ${res.stops[0].name || "stop 1"} by closest next stop${res.unplaced ? ` · ${res.unplaced} without a map address put at the end` : ""}`);
  });

  const routeNumber = () => {
    const n = {};
    stops.forEach((s) => { const r = String(s.route || "").trim(); if (r) n[r] = (n[r] || 0) + 1; });
    return Object.keys(n).sort((a, b) => n[b] - n[a])[0] || "";
  };

  const saveList = async (fallback = "") => {
    const nm = name.trim() || fallback;
    if (!nm) throw new Error("Give the route a name first");
    if (!name.trim()) setName(nm);
    const body = { name: nm, stops: stops.map(({ number, db_name, ...s }) => s) };
    const { data } = listId ? await api.put(`/route-lists/${listId}`, body) : await api.post("/route-lists", body);
    setListId(data.id); await loadLists();
    return data;
  };

  const save = () => run("save", async () => { const data = await saveList(); toast.success(`Route "${data.name}" saved`); });

  const remove = () => run("delete", async () => {
    if (!window.confirm(`Delete saved route "${name}"? Invoices already created are kept.`)) return;
    await api.delete(`/route-lists/${listId}`); reset(); await loadLists(); toast.success("Route deleted");
  });

  const fillDown = () => {
    const start = stops.findIndex((s) => String(s.number || "").trim());
    const m = start >= 0 && String(stops[start].number).trim().match(/^(.*?)(\d+)$/);
    if (!m) { toast.error("Type the first invoice number (ending in digits) on the first stop, then fill down"); return; }
    const [, prefix, digits] = m;
    setStops((prev) => prev.map((s, i) => (i <= start ? s : { ...s, number: `${prefix}${String(Number(digits) + i - start).padStart(digits.length, "0")}` })));
    toast.success(`Filled ${stops.length - start - 1} invoice numbers down from ${stops[start].number}`);
  };

  const create = (final = true) => run("final", async () => {
    const blank = stops.filter((s) => !String(s.number || "").trim()).length;
    if (blank) throw new Error(`${blank} stop${blank === 1 ? " has" : "s have"} no invoice number. Type the first one and use Fill down, or type each one.`);
    if (final && !window.confirm(`Create ${stops.length} final unpaid invoices (no drafts) for every stop?`)) return;
    const routeNo = routeNumber();
    const list = await saveList(routeNo);
    const counts = {};
    stops.forEach((s) => { const d = (s.date || today()).slice(0, 10); counts[d] = (counts[d] || 0) + 1; });
    const runDate = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    const dbName = `${routeNo || list.name} · ${shortDate(runDate)}`;
    const [{ data: src }, { data: settings }] = await Promise.all([api.post("/route-setup/records", { stops }), api.get("/invoice-settings")]);
    const invoices = stops.filter((s) => src.records[s.record_id]).map((s, i) => ({
      ...prefillFromRecord(src.records[s.record_id], src.tables[`${s.db_id}|${s.table}`], s.db_id, s.table, settings),
      number: String(s.number || "").trim(), service_date: s.date || today(), status: "draft", date_paid: null, parts: 0, route_order: i + 1,
    }));
    if (!invoices.length) throw new Error("None of these customers could be found in the database");
    const { data } = await api.post("/invoices/batch", { invoices });
    if (!data.created.length && data.skipped.length) toast.warning(`No new invoices: ${data.skipped.length} skipped — preview or override them below`);
    const out = await finishRun({ ...data, sent: invoices, dbName, listId: list.id, final });
    if (out.invoices.length) toast.success(`Created ${out.invoices.length} ${final ? "unpaid" : "draft"} invoice${out.invoices.length === 1 ? "" : "s"}${out.routeDb ? ` and moved them into sub database "${out.routeDb.name}"` : ""}`);
  });

  const finishRun = async (run) => {
    const made = run.created.length ? (await api.get("/invoices", { params: { batch_id: run.batch_id } })).data : [];
    const drafts = made.filter((x) => x.status === "draft");
    if (run.final && drafts.length) {
      await api.post("/invoices/finalize", { ids: drafts.map((x) => x.id) });
      drafts.forEach((x) => { x.status = "unpaid"; });
    }
    let routeDb = run.routeDb || null;
    if (made.length) {
      routeDb = (await api.post("/route-setup/database", { name: run.dbName, list_id: run.listId, batch_id: run.batch_id, db_id: routeDb?.id })).data;
      window.dispatchEvent(new Event("databases-changed"));
    }
    const out = { ...run, invoices: made, routeDb };
    setResult(out);
    return out;
  };

  return (
    <div className="fade-up space-y-6" data-testid="route-setup-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Schedule</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Route setup</h1>
        <p className="text-sm text-muted-foreground mt-2">Pull customers from your main database, put them in order, pick dates and numbers, then click Invoice all. Edits on route invoices never change the main database.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={listId || "new"} onValueChange={(v) => (v === "new" ? reset() : open(v))}>
          <SelectTrigger className="w-60" data-testid="route-list-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card">
            <SelectItem value="new" data-testid="route-list-new">+ New route</SelectItem>
            {lists.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`route-list-${l.id}`}>{l.name} · {l.stops.length} stops</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Route name, e.g. B083 Waco" maxLength={120} className="w-64" data-testid="route-name-input" />
        <Button onClick={save} disabled={!!busy} data-testid="route-save-button">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save route</Button>
        <Button variant="outline" onClick={() => create(true)} disabled={!stops.length || !!busy} data-testid="route-invoice-all">{busy === "final" ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Invoice all</Button>
        {listId && <Button variant="ghost" onClick={remove} disabled={!!busy} className="text-destructive" data-testid="route-delete-button"><Trash2 size={14} /> Delete</Button>}
      </div>

      <RoutePullBar onAdd={add} />

      <div className="flex flex-wrap items-center gap-2" data-testid="route-toolbar">
        <span className="text-sm text-muted-foreground" data-testid="route-stop-count">{stops.length} stops{selected.size ? ` · ${selected.size} checked` : ""}</span>
        <Button variant="outline" size="sm" onClick={() => setStops((p) => [...p].sort(byJob))} disabled={!stops.length} data-testid="route-sort-job"><ArrowDownWideNarrow size={14} /> Order by Job #</Button>
        <Button variant="outline" size="sm" onClick={() => closest(0)} disabled={stops.length < 3 || !!busy} data-testid="route-sort-closest">
          {busy === "closest" ? <Loader2 size={14} className="animate-spin" /> : <Route size={14} />} Order by closest from stop 1</Button>
        <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Invoice date for {selected.size ? "checked" : "all"}:</span>
          <div className="w-48"><DateField id="route-bulk-date" value={bulkDate} onChange={applyDate} /></div></div>
        <Button variant="outline" size="sm" onClick={fillDown} disabled={stops.length < 2} data-testid="route-fill-down"><ArrowDownToLine size={14} /> Fill down from first number</Button>
        {selected.size > 0 && <Button variant="ghost" size="sm" onClick={() => { setStops((p) => p.filter((s) => !selected.has(s.record_id))); setSelected(new Set()); }} data-testid="route-remove-checked"><Trash2 size={14} /> Remove checked</Button>}
        <Button variant={showMap ? "secondary" : "outline"} size="sm" onClick={() => setShowMap((v) => !v)} disabled={!stops.length} data-testid="route-map-toggle"><MapIcon size={14} /> {showMap ? "Hide map" : "Show map"}</Button>
      </div>

      {result && <RouteResult result={result} onOverride={finishRun} onFinalized={(ids) => setResult((r) => ({ ...r, invoices: r.invoices.map((x) => (ids.includes(x.id) ? { ...x, status: "unpaid" } : x)) }))} />}
      {showMap && stops.length > 0 && <RouteMapPanel stops={stops} onStartHere={(i) => closest(i)} onReorder={setStops} />}
      <RouteStopsTable stops={stops} setStops={setStops} selected={selected} setSelected={setSelected} />
    </div>
  );
}
